import React, { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Users, BarChart3, ClipboardList, FolderOpen, Activity, Menu, Circle, ChevronDown, LogOut, Shield, Star, Crown, Dumbbell } from 'lucide-react';
import { useReferenceData } from './ReferenceDataContext';
import { ShareSelectionModal } from './TeamSelection';
import { AddSelectionModal } from './TeamSelection';
import type { LucideIcon } from 'lucide-react';

interface NavigationProps {
  currentUser: string | null;
  isAdmin: boolean;
  isSuperUser: boolean;
  onLogout: () => void;
}

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
}

const navItems: NavItem[] = [
  { path: '/team-selection', label: 'Team Selection', icon: Users },
  { path: '/squad-analysis', label: 'Squad Analysis', icon: BarChart3 },
  { path: '/training-planner', label: 'Training Planner', icon: ClipboardList },
  { path: '/training-sessions', label: 'Training Sessions', icon: FolderOpen },
  { path: '/training-drills', label: 'Training Drills', icon: Activity },
];

export function Navigation({ currentUser, isAdmin, isSuperUser, onLogout }: NavigationProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { referenceData, selectedSelectionId, setSelectedSelectionId, refetch } = useReferenceData();

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { currentShareType } = useReferenceData();
  const currentSelection = referenceData?.selections.find(s => s.id === selectedSelectionId);
  const canShare = currentSelection?.canShare ?? false;
  const isPlayerView = currentShareType === 'Player';

  const playerNavItems: NavItem[] = [
    { path: '/training', label: 'Training', icon: Dumbbell },
  ];

  const visibleNavItems = isPlayerView ? playerNavItems : navItems;

  return (
    <div className="nav-container">
      {/* Main Header */}
      <div className="nav-header">
        <div className="nav-brand">
          <div className="nav-logo">
            <Circle size={24} fill="currentColor" />
          </div>
          <div>
            <h2 className="nav-title">Team Selection</h2>
            <div className="nav-tagline">Build balanced teams effortlessly</div>
          </div>
        </div>

        <div className="nav-user">
          {referenceData && referenceData.selections.length > 0 && (
            <div className="nav-selection">
              <select
                id="selection-dropdown"
                value={selectedSelectionId ?? ''}
                onChange={(e) => setSelectedSelectionId(Number(e.target.value))}
                className="nav-selection-dropdown"
              >
                {referenceData.selections.map((selection) => (
                  <option key={selection.id} value={selection.id}>
                    {selection.name}{selection.sport && selection.sport !== 'Football' ? ` (${selection.sport})` : ''}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="btn btn-sm btn-outline-primary"
                title="Add new selection"
              >
                Add
              </button>
              {canShare && (
                <button
                  onClick={() => setIsShareModalOpen(true)}
                  className="btn btn-sm btn-outline-primary"
                  title="Share this selection"
                >
                  Share
                </button>
              )}
            </div>
          )}
          <div className="nav-user-dropdown" ref={userMenuRef}>
            <button
              className="nav-user-trigger"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            >
              <span className="nav-user-name">{currentUser}</span>
              {isAdmin && <Shield size={14} className="nav-user-admin-icon" />}
              {isSuperUser && <Star size={14} className="nav-user-admin-icon" />}
              <ChevronDown size={16} className={`nav-user-chevron ${isUserMenuOpen ? 'nav-user-chevron-open' : ''}`} />
            </button>
            {isUserMenuOpen && (
              <div className="nav-user-menu">
                <div className="nav-user-menu-header">
                  <span className="nav-user-menu-name">{currentUser}</span>
                  {isAdmin && <span className="badge-admin">Admin</span>}
                  {isSuperUser && <span className="badge-admin">Super</span>}
                </div>
                <div className="nav-user-menu-divider" />
                <button onClick={onLogout} className="nav-user-menu-item nav-user-menu-logout">
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Bar */}
      <div className="nav-bar">
        {/* Desktop Navigation */}
        <nav className="nav-links">
          {visibleNavItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  isActive ? 'nav-link nav-link-active' : 'nav-link'
                }
              >
                <IconComponent size={18} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
          {isSuperUser && (
            <NavLink
              to="/admin-dashboard"
              className={({ isActive }) =>
                isActive ? 'nav-link nav-link-active' : 'nav-link'
              }
            >
              <Crown size={18} />
              <span>Admin Dashboard</span>
            </NavLink>
          )}
        </nav>

        {/* Mobile Menu Button (visible on small screens via CSS media query if needed) */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="nav-mobile-toggle"
          aria-label="Toggle navigation menu"
        >
          <Menu size={20} />
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="nav-mobile-menu">
          {visibleNavItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  isActive ? 'nav-link nav-link-active w-full' : 'nav-link w-full'
                }
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <IconComponent size={18} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
          {isSuperUser && (
            <NavLink
              to="/admin-dashboard"
              className={({ isActive }) =>
                isActive ? 'nav-link nav-link-active w-full' : 'nav-link w-full'
              }
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Crown size={18} />
              <span>Admin Dashboard</span>
            </NavLink>
          )}
        </div>
      )}

      {/* Share Selection Modal */}
      {isShareModalOpen && currentSelection && (
        <ShareSelectionModal
          selectionId={currentSelection.id}
          selectionName={currentSelection.name}
          onClose={() => setIsShareModalOpen(false)}
          onSuccess={() => {
            // Modal closes automatically on success
          }}
        />
      )}

      {/* Add Selection Modal */}
      {isAddModalOpen && (
        <AddSelectionModal
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={async (newSelection) => {
            // Refetch to update the selections list
            await refetch();
            // Set the new selection as active
            setSelectedSelectionId(newSelection.id);
          }}
        />
      )}
    </div>
  );
}
